/**
 * T072: Storage Redux Actions
 * Actions for storage providers and policies
 */

import axios from 'axios';
import { showMessage } from 'app/store/actions/fuse';
import { API_BASE_URL } from '@config';

// Action Types
export const STORAGE_PROVIDERS_LOADING = '[STORAGE] PROVIDERS LOADING';
export const STORAGE_PROVIDERS_SUCCESS = '[STORAGE] PROVIDERS SUCCESS';
export const STORAGE_PROVIDERS_ERROR = '[STORAGE] PROVIDERS ERROR';
export const STORAGE_PROVIDER_SELECT = '[STORAGE] PROVIDER SELECT';
export const STORAGE_PROVIDER_CREATE_SUCCESS = '[STORAGE] PROVIDER CREATE SUCCESS';
export const STORAGE_PROVIDER_UPDATE_SUCCESS = '[STORAGE] PROVIDER UPDATE SUCCESS';
export const STORAGE_PROVIDER_DELETE_SUCCESS = '[STORAGE] PROVIDER DELETE SUCCESS';

export const STORAGE_POLICIES_LOADING = '[STORAGE] POLICIES LOADING';
export const STORAGE_POLICIES_SUCCESS = '[STORAGE] POLICIES SUCCESS';
export const STORAGE_POLICIES_ERROR = '[STORAGE] POLICIES ERROR';
export const STORAGE_PREDEFINED_POLICIES_SUCCESS = '[STORAGE] PREDEFINED POLICIES SUCCESS';
export const STORAGE_POLICY_SELECT = '[STORAGE] POLICY SELECT';
export const STORAGE_POLICY_CREATE_SUCCESS = '[STORAGE] POLICY CREATE SUCCESS';
export const STORAGE_POLICY_UPDATE_SUCCESS = '[STORAGE] POLICY UPDATE SUCCESS';
export const STORAGE_POLICY_DELETE_SUCCESS = '[STORAGE] POLICY DELETE SUCCESS';

export const STORAGE_PROVIDER_FORM_OPEN = '[STORAGE] PROVIDER FORM OPEN';
export const STORAGE_PROVIDER_FORM_CLOSE = '[STORAGE] PROVIDER FORM CLOSE';
export const STORAGE_POLICY_FORM_OPEN = '[STORAGE] POLICY FORM OPEN';
export const STORAGE_POLICY_FORM_CLOSE = '[STORAGE] POLICY FORM CLOSE';

export const STORAGE_CLEAR = '[STORAGE] CLEAR';

// Helper to build headers with optional organizationId
const getHeaders = (organizationId = null) => ({
	headers: {
		...(organizationId ? { 'x-organization-id': organizationId } : {})
	}
});

// Storage Providers Actions
export function getStorageProviders(organizationId = null) {
	return (dispatch) => {
		dispatch({ type: STORAGE_PROVIDERS_LOADING });

		return axios
			.get(`${API_BASE_URL}/api/storage-providers`, { ...getHeaders(organizationId), skipForbiddenToast: true })
			.then((response) => {
				dispatch({
					type: STORAGE_PROVIDERS_SUCCESS,
					payload: response.data
				});
			})
			.catch((error) => {
				dispatch({
					type: STORAGE_PROVIDERS_ERROR,
					payload: error.response?.data?.message || 'Failed to load storage providers'
				});
				// 401/403 = the viewer simply lacks storage permissions (e.g.
				// POC/demo org admins) - the section renders empty; no red toast.
				const status = error.response?.status;
				if (status !== 401 && status !== 403) {
					dispatch(showMessage({ message: 'Failed to load storage providers', variant: 'error' }));
				}
			});
	};
}

export function createStorageProvider(providerData) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/storage-providers`, providerData)
			.then((response) => {
				dispatch({
					type: STORAGE_PROVIDER_CREATE_SUCCESS,
					payload: response.data
				});
				dispatch(showMessage({ message: 'Storage provider created successfully', variant: 'success' }));
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to create storage provider';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function updateStorageProvider(providerId, providerData) {
	return (dispatch) => {
		return axios
			.put(`${API_BASE_URL}/api/storage-providers/${providerId}`, providerData)
			.then((response) => {
				dispatch({
					type: STORAGE_PROVIDER_UPDATE_SUCCESS,
					payload: response.data
				});
				dispatch(showMessage({ message: 'Storage provider updated successfully', variant: 'success' }));
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to update storage provider';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function deleteStorageProvider(providerId) {
	return (dispatch) => {
		return axios
			.delete(`${API_BASE_URL}/api/storage-providers/${providerId}`)
			.then(() => {
				dispatch({
					type: STORAGE_PROVIDER_DELETE_SUCCESS,
					payload: providerId
				});
				dispatch(showMessage({ message: 'Storage provider deleted successfully', variant: 'success' }));
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to delete storage provider';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function selectStorageProvider(provider) {
	return {
		type: STORAGE_PROVIDER_SELECT,
		payload: provider
	};
}

// Storage Policies Actions
export function getStoragePolicies(providerId = null, organizationId = null) {
	return (dispatch) => {
		dispatch({ type: STORAGE_POLICIES_LOADING });

		const url = providerId
			? `${API_BASE_URL}/api/storage-policies?provider=${providerId}`
			: `${API_BASE_URL}/api/storage-policies`;

		return axios
			.get(url, getHeaders(organizationId))
			.then((response) => {
				dispatch({
					type: STORAGE_POLICIES_SUCCESS,
					payload: response.data
				});
			})
			.catch((error) => {
				dispatch({
					type: STORAGE_POLICIES_ERROR,
					payload: error.response?.data?.message || 'Failed to load storage policies'
				});
				dispatch(showMessage({ message: 'Failed to load storage policies', variant: 'error' }));
			});
	};
}

export function getPredefinedPolicies() {
	return (dispatch) => {
		return axios
			.get(`${API_BASE_URL}/api/storage-policies/predefined`)
			.then((response) => {
				dispatch({
					type: STORAGE_PREDEFINED_POLICIES_SUCCESS,
					payload: response.data
				});
			})
			.catch((error) => {
				dispatch(showMessage({ message: 'Failed to load predefined policies', variant: 'error' }));
			});
	};
}

export function createStoragePolicy(policyData) {
	return (dispatch) => {
		return axios
			.post(`${API_BASE_URL}/api/storage-policies`, policyData)
			.then((response) => {
				dispatch({
					type: STORAGE_POLICY_CREATE_SUCCESS,
					payload: response.data
				});
				dispatch(showMessage({ message: 'Storage policy created successfully', variant: 'success' }));
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to create storage policy';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function updateStoragePolicy(policyId, policyData) {
	return (dispatch) => {
		return axios
			.put(`${API_BASE_URL}/api/storage-policies/${policyId}`, policyData)
			.then((response) => {
				dispatch({
					type: STORAGE_POLICY_UPDATE_SUCCESS,
					payload: response.data
				});
				dispatch(showMessage({ message: 'Storage policy updated successfully', variant: 'success' }));
				return response.data;
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to update storage policy';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function deleteStoragePolicy(policyId, organizationId = null) {
	return (dispatch) => {
		return axios
			.delete(`${API_BASE_URL}/api/storage-policies/${policyId}`, getHeaders(organizationId))
			.then(() => {
				dispatch({
					type: STORAGE_POLICY_DELETE_SUCCESS,
					payload: policyId
				});
				dispatch(showMessage({ message: 'Storage policy deleted successfully', variant: 'success' }));
			})
			.catch((error) => {
				const errorMsg = error.response?.data?.message || 'Failed to delete storage policy';
				dispatch(showMessage({ message: errorMsg, variant: 'error' }));
				throw error;
			});
	};
}

export function selectStoragePolicy(policy) {
	return {
		type: STORAGE_POLICY_SELECT,
		payload: policy
	};
}

// UI Actions
export function openProviderForm(provider = null) {
	return (dispatch) => {
		if (provider) {
			dispatch(selectStorageProvider(provider));
		}
		dispatch({ type: STORAGE_PROVIDER_FORM_OPEN });
	};
}

export function closeProviderForm() {
	return {
		type: STORAGE_PROVIDER_FORM_CLOSE
	};
}

export function openPolicyForm(policy = null) {
	return (dispatch) => {
		if (policy) {
			dispatch(selectStoragePolicy(policy));
		}
		dispatch({ type: STORAGE_POLICY_FORM_OPEN });
	};
}

export function closePolicyForm() {
	return {
		type: STORAGE_POLICY_FORM_CLOSE
	};
}

export function clearStorage() {
	return {
		type: STORAGE_CLEAR
	};
}
